import { ref } from "vue";
import api from "../api";
import { recordRoutingStat, summarizeRoutingStats } from "../utils/routingStats";
import { getRoutingMode } from "./routingMode";
import { clearClientRoutingCache, prefetchRoutingAround, routeClient, setRoutingStoreKind, setWarmAhead, warmRoutingAround } from "./useClientRouting";

export type RoutingStrategy = "shortest" | "prefer_trails" | "prefer_roads";
export type RoutingStatus = "routed" | "no_network" | "disconnected" | "too_long" | "blocked";
export type Coordinate = [number, number];

export interface RoutedPath {
  status: RoutingStatus;
  geometry: Coordinate[];
  start_attachment: Coordinate | null;
  end_attachment: Coordinate | null;
  connector_distance_m: number;
  network_distance_m: number;
  total_distance_m: number;
}

export interface RoutePathOptions {
  strategy?: RoutingStrategy;
  destinationMode?: "network" | "poi";
  snapRadiusM?: number;
}

const serverRoute = async (start: Coordinate, destination: Coordinate, options: RoutePathOptions): Promise<RoutedPath> => {
  const { data } = await api.post<RoutedPath>("/routing/path", {
    start,
    destination,
    strategy: options.strategy ?? "shortest",
    destination_mode: options.destinationMode ?? "network",
    snap_radius_m: Math.min(options.snapRadiusM ?? 100, 100),
  });
  return data;
};

// Клиентский расчёт не должен ломать маршрутизацию: любая ошибка - откат на сервер.
const routeClientSafe = async (start: Coordinate, destination: Coordinate, options: RoutePathOptions) => {
  try {
    return await routeClient(start, destination, { destinationMode: options.destinationMode, snapRadiusM: options.snapRadiusM });
  } catch {
    return null;
  }
};

export function useRoutingPath() {
  const pending = ref(false);
  const lastStatus = ref<RoutingStatus | null>(null);
  let requestId = 0;

  const route = async (
    start: Coordinate,
    destination: Coordinate,
    options: RoutePathOptions = {}
  ): Promise<RoutedPath | null> => {
    const current = ++requestId;
    pending.value = true;
    try {
      const mode = getRoutingMode();
      let data: RoutedPath | null = null;
      if (mode === "client") {
        // Клиентский роутер - единственный путь: тот же алгоритм, что на сервере,
        // поэтому отката на сервер нет. null (нет тайлов) - вызывающий рисует свой fallback.
        const c = await routeClientSafe(start, destination, options);
        if (c) {
          recordRoutingStat(c.stat);
          data = c.result;
        }
      } else {
        const t0 = performance.now();
        data = await serverRoute(start, destination, options);
        recordRoutingStat({ mode: "server", ms: performance.now() - t0, status: data.status });
        if (mode === "both") {
          const c = await routeClientSafe(start, destination, options);
          if (c) {
            recordRoutingStat(c.stat);
            console.info("[routing parity]", {
              statusEqual: c.result.status === data.status,
              server: data.status,
              client: c.result.status,
              distanceDeltaM: c.result.total_distance_m - data.total_distance_m,
            });
          }
        }
      }
      if (!data) {
        if (current === requestId) lastStatus.value = "disconnected";
        return null;
      }
      if (current !== requestId) return null;
      lastStatus.value = data.status;
      return data;
    } catch {
      if (current === requestId) lastStatus.value = "disconnected";
      return null;
    } finally {
      if (current === requestId) pending.value = false;
    }
  };

  const cancel = () => {
    requestId++;
    pending.value = false;
  };

  const reset = () => {
    cancel();
    lastStatus.value = null;
  };

  return { pending, lastStatus, route, cancel, reset };
}

// Хук для замеров (scripts/routing-bench) - только вне production-сборки.
if (typeof window !== "undefined" && import.meta.env.VITE_APP_ENV !== "production") {
  (window as unknown as { __ecowayRoutingBench?: unknown }).__ecowayRoutingBench = {
    server: async (start: Coordinate, destination: Coordinate, options: RoutePathOptions = {}) => {
      const t0 = performance.now();
      const result = await serverRoute(start, destination, options);
      return { result, ms: performance.now() - t0 };
    },
    client: (start: Coordinate, destination: Coordinate, options: RoutePathOptions = {}) => routeClientSafe(start, destination, options),
    clearCache: clearClientRoutingCache,
    prefetch: prefetchRoutingAround,
    setWarmAhead,
    warm: warmRoutingAround,
    // сценарии замера: пустое хранилище (холодный старт) или настоящий IndexedDB
    useFreshStore: () => setRoutingStoreKind("memory"),
    useIdbStore: () => setRoutingStoreKind("idb"),
    summarize: summarizeRoutingStats,
  };
}
